import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    TextField,
    Box,
    Typography,
    Divider,
    Stack,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import './eventForm.css';

function addTwoHours(time) {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours + 2, minutes);
    return date.toTimeString().slice(0, 5);
}

export default function EventScheduleDialog({
    open,
    onClose,
    dateTimes,
    timezone,
    err,
    onAddSchedule,
    onDeleteSchedule,
    onAddSlot,
    onDeleteSlot,
    onDateChange,
    onSlotChange,
    onSave,
}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <EventIcon color="primary" />
                    <span>Event schedules</span>
                </Stack>
                {timezone ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Times are in {timezone} (event local)
                    </Typography>
                ) : null}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {dateTimes.map((dateTime, dateIndex) => (
                        <Box
                            key={`schedule-${dateIndex}`}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                                <TextField
                                    label="Date"
                                    type="date"
                                    value={dateTime.date || ''}
                                    onChange={(e) => onDateChange(dateIndex, 'date', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    size="small"
                                />
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => onAddSlot(dateIndex)}
                                    >
                                        Add slot
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={() => onDeleteSchedule(dateIndex)}
                                        disabled={dateTimes.length <= 1}
                                    >
                                        Remove day
                                    </Button>
                                </Stack>
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />

                            <Stack spacing={1.5}>
                                {(dateTime.slots || []).map((slot, slotIndex) => (
                                    <Stack
                                        key={`slot-${dateIndex}-${slotIndex}`}
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                        alignItems={{ sm: 'center' }}
                                    >
                                        <AccessTimeIcon fontSize="small" color="action" />
                                        <TextField
                                            label="Start"
                                            type="time"
                                            value={slot.startTime || ''}
                                            onChange={(e) => onSlotChange(dateIndex, slotIndex, 'startTime', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{ minWidth: 140 }}
                                        />
                                        <TextField
                                            label="End"
                                            type="time"
                                            value={slot.endTime || ''}
                                            onChange={(e) => onSlotChange(dateIndex, slotIndex, 'endTime', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{ minWidth: 140 }}
                                        />
                                        <IconButton
                                            color="error"
                                            onClick={() => onDeleteSlot(dateIndex, slotIndex)}
                                            disabled={(dateTime.slots || []).length <= 1}
                                            aria-label="Delete slot"
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>

                            {err && (dateTime.date === '' || (dateTime.slots || []).some((slot) => !slot.startTime || !slot.endTime)) ? (
                                <Typography className="event-form-error" sx={{ mt: 1 }}>
                                    Each schedule needs a date and complete time slots.
                                </Typography>
                            ) : null}
                        </Box>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddSchedule}>
                    Add another day
                </Button>
                <Box>
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={onSave}>
                        Save schedules
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export { addTwoHours };
